/**
 * /comparisons/coda — "Draftlize vs Coda" comparison page. Targets PMs
 * who use Coda for product specs / planning.
 */
import { MarketingShell } from '../MarketingShell';

export function Coda() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/comparisons/coda"
      documentTitle="Coda Alternative for AI-Native Product Teams: Draftlize vs Coda · Draftlize"
      documentDescription="A Coda alternative for teams running product work with AI agents. Coda is documents-with-database-superpowers; Draftlize is a decision substrate AI can read and write. Compared for PMs."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Comparison · vs Coda</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}
          >
            Draftlize vs Coda:<br/>
            <em>documents vs substrates.</em>
          </h1>
          <p className="lede">
            Coda combined documents and databases years before that was cool. For structured planning grids, vendor matrices, and OKR trackers, Coda is genuinely best-in-class. But the question Draftlize answers — "where does my AI agent keep state across chats" — isn't what Coda was built for.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">Coda is a tool. <em>Draftlize is a substrate.</em></h2>
          </div>
          <p>Coda lets you build powerful documents. Draftlize is something AI agents build *with*. Both legitimate, very different.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Coda</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Documents + tables + databases</td><td className="no">—</td><td className="yes">excellent</td></tr>
            <tr><td>Custom packs + formula language</td><td className="no">—</td><td className="yes">native</td></tr>
            <tr><td>AI assistant inside documents</td><td className="partial">agent IS the substrate</td><td className="yes">Coda AI</td></tr>
            <tr><td>AI agent reads decisions across docs as context</td><td className="ours yes">native</td><td className="partial">via @-mentions</td></tr>
            <tr><td>AI agent writes structured cards back</td><td className="ours yes">native</td><td className="partial">edits doc cells</td></tr>
            <tr><td>Dependency tracking + stale propagation</td><td className="ours yes">native</td><td className="no">—</td></tr>
            <tr><td>External AI tools (Claude Code / Cursor) read it</td><td className="ours yes">MCP + CLI</td><td className="no">—</td></tr>
            <tr><td>Team workspace + permissions</td><td className="no">solo (v1)</td><td className="yes">excellent</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Where <em>Coda wins.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Structured tools for the team</h3>
            <p>OKR trackers, vendor scoring, sprint dashboards, hiring pipelines — building those as Coda docs gets you a real tool, not just a wiki page. Draftlize doesn't try to be a database.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">If you want to *build* an internal tool</h3>
            <p>Coda's formula language + packs make it a low-code platform. Need a custom calculator, a workflow with conditional logic, a connected airtable replacement? Coda. Draftlize would be the wrong shape.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">For teams that live in docs together</h3>
            <p>Coda's collaboration model is mature. Multi-user real-time, fine-grained permissions, comments. Draftlize is solo-first by design.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Where <em>Draftlize wins.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">When your "document" is a graph, not a doc</h3>
            <p>Product thinking has decisions that depend on other decisions. A doc represents that as paragraphs. A graph represents it as <em>edges</em>. When you change a card, the graph knows what depends on it.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">When AI is doing half the writing</h3>
            <p>Coda AI is great for "rewrite this paragraph." Draftlize agents are great for "we discussed pricing 3 weeks ago, here's what we decided and why, let me update the dependent cards." Different mode of AI collaboration.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">When your AI tools span multiple apps</h3>
            <p>Coda AI lives in Coda. Draftlize cards land in your repo via CLI, surface via MCP in Claude Code / Cursor / Desktop. Your decisions are addressable from anywhere — not locked to one canvas.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Coda lets you build a great document. Draftlize lets your AI agent build *with* a great substrate.
        </blockquote>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start free with <em>$5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            New accounts start with $5 free, pay only for what you use, balance never expires. Coda for structured tools. Draftlize for AI-assisted thinking.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
